import { Lock } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSessionStore, setSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import YearStepper from "app/pages/InteractiveProposal/YearStepper";
import { useIRCContext } from "../IRCContext";

const formatOpenDate = (d: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : "—";

function LockScreen() {
  const {
    lockState,
    reviewOpenDate,
    calendarYear,
    isContact,
    yearSteps,
    selectedYear,
    selectedCreditType,
  } = useIRCContext();
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const { pathname } = useLocation();
  const isSummaryRoute = pathname.includes("/interactive-review-center/summary");
  const navigate = useNavigate();

  const yearStepper = isSummaryRoute && yearSteps.length > 0 && (
    <YearStepper
      isDark={isDark}
      steps={yearSteps}
      phases={["preCredit", "postCredit"]}
      selectedPhase={selectedCreditType}
      selectedYearNumber={selectedYear}
      allowLockedClick
      onYearClick={(step) => {
        setSessionStore("ircSelectedYear", step.yearNumber);
        window.dispatchEvent(
          new CustomEvent("ircSelectionChanged", { detail: { year: step.yearNumber } }),
        );
      }}
      onPhaseClick={(phase) => {
        setSessionStore("ircSelectedCreditType", phase);
        window.dispatchEvent(
          new CustomEvent("ircSelectionChanged", { detail: { creditType: phase } }),
        );
      }}
    />
  );

  if (lockState === "pre_window") {
    return (
      <div className={`h-full w-full bg-[#E2E8F0] ${isSummaryRoute ? "mt-[10%]" : ""}`}>
        {yearStepper}
        <div className="flex flex-col items-center justify-center gap-8 p-4 h-full w-full">
          <div className="flex items-center gap-8">
            <Lock size={61} strokeWidth={2} color="#004781" />
            <span className="text-6xl font-bold conthrax-font text-[#004781]">REVIEW LOCKED</span>
            <Lock size={61} strokeWidth={2} color="#004781" />
          </div>
          <span className="text-2xl text-center text-[#191A1C]">
            This page will be open to view after{" "}
            <span className="font-semibold">{formatOpenDate(reviewOpenDate)}</span>
          </span>
        </div>
      </div>
    );
  }

  // fee_locked
  return (
    <div className={`h-full w-full bg-[#E2E8F0] ${isSummaryRoute ? "mt-[9%]" : ""}`}>
      {yearStepper}
      <div className="flex flex-col items-center justify-center p-4 h-full w-full">
        <div className="flex items-center gap-[32px]">
          <Lock size={61} strokeWidth={2} color="#004781" />
          <span className="text-6xl font-bold conthrax-font text-[#004781]">REVIEW LOCKED</span>
          <Lock size={61} strokeWidth={2} color="#004781" />
        </div>
        <div className="flex flex-col items-center justify-center px-12 gap-[24px] mt-[32px]">
          <span className="text-[24px] font-thin text-center text-[#191A1C]">
            Your annual plan review for {calendarYear} is currently unavailable due to unpaid plan
            fees.
          </span>
          <span className="text-[24px] text-center font-thin text-[#191A1C]">
            To continue with your personalized planning experience and unlock this year&apos;s
            review, please complete your payment.
          </span>
          <span className="text-[24px] text-center font-thin text-[#191A1C]">
            You still have full access to your prior years&apos; reviews and plan history.
          </span>
          <span className="text-[24px] text-center font-thin text-[#191A1C]">
            Pay now to resume your active review and ensure no disruption in your insurance planning
            journey.
          </span>
        </div>
        {isContact && (
          <div className="flex items-center justify-center mt-[54px]">
            <div
              className="text-[#FFFFFF] text-[24px] conthrax-font bg-[#004781] rounded-full px-10 py-4 cursor-pointer"
              onClick={() => navigate("/plan-summary/plan?tab=plan")}
            >
              Pay Now
            </div>
          </div>
        )}
        <span className="text-2xl text-center text-[#6C757D] mt-4 font-normal">
          Need help? Contact your advisor for support.
        </span>
      </div>
    </div>
  );
}

export default LockScreen;
